import { Layout, type Node } from "@revideo/2d";
import { timelineBehindTexts, type BehindText, type Clip } from "@hudhud/core";
import { BEHIND_IN, HEIGHT, WIDTH, behindMotion, msToFrame } from "@hudhud/reel/math";
import { isRtlText, paragraph } from "./text";

/** One big word block: centred in the upper third unless nudged; the speaker's cutout is drawn over it. */
function block(item: BehindText, font: string, frame: () => number, frames: number): Node {
  const runs = item.text.split(/(\*[^*]+\*)/g).filter(Boolean).map((part) => {
    const accent = part.startsWith("*") && part.endsWith("*") && part.length > 2;
    return { text: accent ? part.slice(1, -1) : part, accent };
  });
  const m = () => behindMotion(item, frame(), frames);
  const rtl = isRtlText(item.text);
  return new Layout({
    layout: true, width: WIDTH - 80, justifyContent: "center", offset: [0, -1],
    position: () => [item.offsetX, HEIGHT * 0.22 + item.offsetY - HEIGHT / 2 + m().ty],
    opacity: () => m().opacity, cachePadding: rtl ? [item.size, WIDTH] : 0,
    children: [paragraph({ runs, font, weight: 900, size: item.size, maxWidth: WIDTH - 80, fill: item.color, accentFill: item.accentColor, align: "center", lineHeight: rtl ? 1.3 : 0.92, letterSpacing: rtl ? 0 : -item.size * 0.035, rtl, uppercase: item.uppercase, preLine: true })],
  });
}

/** The behind-the-speaker texts that fall inside one video piece, on the piece's own clock (`pieceFrom` = its first timeline frame). */
export function behindTexts(clip: Clip, pieceFrom: number, pieceFrames: number, frame: () => number): Node | null {
  const items = timelineBehindTexts(clip).map(({ item, range }) => {
    const from = msToFrame(range.inMs) - pieceFrom;
    const frames = Math.max(BEHIND_IN * 2, msToFrame(range.outMs) - msToFrame(range.inMs));
    if (from >= pieceFrames || from + frames <= 0) return null;
    const local = () => frame() - from;
    const on = () => { const l = local(); return l >= 0 && l < frames; };
    return new Layout({ width: WIDTH, height: HEIGHT, opacity: () => (on() ? 1 : 0), children: [block(item, item.font ?? clip.style.font, local, frames)] });
  }).filter((n): n is Layout => n !== null);
  return items.length ? new Layout({ width: WIDTH, height: HEIGHT, children: items }) : null;
}
